import { useState, useEffect, useCallback } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_META = {
  1: { label: "Tier 1 — Critical", color: "#FF6B6B", emoji: "🔴" },
  2: { label: "Tier 2 — Growth", color: "#FFB347", emoji: "🟡" },
  3: { label: "Tier 3 — Polish", color: "#5AC8D8", emoji: "🔵" },
  4: { label: "Tier 4 — Hygiene", color: "#9DA9B8", emoji: "🟢" },
};

const STATUS_META = {
  todo: { label: "To Do", color: "#7E92A8" },
  in_progress: { label: "In Progress", color: "#FFB347" },
  done: { label: "Done", color: "#00DC78" },
  n_a: { label: "N/A", color: "#5A7090" },
};

const cellInputStyle = {
  width: "100%",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.15)",
  color: "#fff",
  padding: "0.45rem 0.55rem",
  borderRadius: 5,
  fontSize: "0.85rem",
};

const labelStyle = {
  display: "block",
  color: "#7E92A8",
  fontSize: "0.66rem",
  fontWeight: 700,
  letterSpacing: "0.10em",
  textTransform: "uppercase",
  marginBottom: "0.25rem",
};

const cardStyle = {
  background: "rgba(255,255,255,0.025)",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 10,
  padding: "1.1rem 1.3rem",
};

const LaunchChecklistTab = () => {
  const [section, setSection] = useState("checklist");
  return (
    <div data-testid="launch-checklist-tab" style={{ padding: "0 0.5rem" }}>
      <div style={{ marginBottom: "1.25rem" }}>
        <h1 style={{ color: "#fff", fontSize: "1.65rem", fontWeight: 800, margin: "0 0 0.4rem" }}>Launch Protections</h1>
        <p style={{ color: "#ADBED1", fontSize: "0.92rem", margin: 0, lineHeight: 1.5, maxWidth: 760 }}>
          The 20-item checklist + legal documents + company info that protect Moovin Forward from <strong style={{ color: "#FF6B6B" }}>EPA fines, lien-rights loss, WC liability, and pierced LLC shield</strong>. Knock these out and you launch with a moat.
        </p>
      </div>

      {/* Section tabs */}
      <div style={{ display: "flex", gap: "0.4rem", marginBottom: "1.2rem", flexWrap: "wrap" }}>
        {[
          { id: "checklist", label: "📋 Checklist" },
          { id: "company", label: "🏢 Company Info" },
          { id: "vendors", label: "🚪 Vendor Tracker" },
          { id: "docs", label: "📄 Legal Docs" },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setSection(t.id)}
            data-testid={`section-${t.id}`}
            style={{
              padding: "0.55rem 1rem",
              background: section === t.id ? "linear-gradient(135deg, #00DC78 0%, #00FFFF 100%)" : "rgba(255,255,255,0.04)",
              border: section === t.id ? "none" : "1px solid rgba(255,255,255,0.10)",
              color: section === t.id ? "#030B1A" : "#ADBED1",
              borderRadius: 6,
              fontSize: "0.82rem",
              fontWeight: 800,
              cursor: "pointer",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {section === "checklist" && <ChecklistSection />}
      {section === "company" && <CompanySection />}
      {section === "vendors" && <VendorsSection />}
      {section === "docs" && <DocsSection />}
    </div>
  );
};

// ─── Checklist Section ───
const ChecklistSection = () => {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ total: 0, done: 0, in_progress: 0, todo: 0, n_a: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/launch-checklist`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setItems(d.items || []);
        setCounts(d.counts || {});
      }
    } catch {}
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const updateItem = async (item_id, patch) => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/launch-checklist/${item_id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (r.ok) {
        const updated = await r.json();
        setItems(prev => prev.map(it => it.item_id === item_id ? updated : it));
        // Recount
        const newItems = items.map(it => it.item_id === item_id ? updated : it);
        setCounts({
          total: newItems.length,
          done: newItems.filter(x => x.status === "done").length,
          in_progress: newItems.filter(x => x.status === "in_progress").length,
          todo: newItems.filter(x => x.status === "todo").length,
          n_a: newItems.filter(x => x.status === "n_a").length,
        });
      }
    } catch {}
  };

  const grouped = [1, 2, 3, 4].map(t => ({ tier: t, items: items.filter(i => i.tier === t) }));
  const pct = counts.total ? Math.round(((counts.done + counts.n_a) / counts.total) * 100) : 0;

  if (loading) return <div style={{ color: "#7E92A8", textAlign: "center", padding: "2rem" }}>Loading checklist…</div>;

  return (
    <>
      {/* Progress strip */}
      <div style={{ ...cardStyle, marginBottom: "1.1rem", padding: "1rem 1.3rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem", flexWrap: "wrap", gap: "0.5rem" }}>
          <div style={{ color: "#fff", fontWeight: 800, fontSize: "1rem" }}>Launch Readiness: {pct}%</div>
          <div style={{ display: "flex", gap: "0.7rem", flexWrap: "wrap", fontSize: "0.78rem" }}>
            <span style={{ color: "#00DC78", fontWeight: 700 }}>✓ {counts.done} Done</span>
            <span style={{ color: "#FFB347", fontWeight: 700 }}>⚙ {counts.in_progress} In Progress</span>
            <span style={{ color: "#7E92A8", fontWeight: 700 }}>○ {counts.todo} To Do</span>
            {counts.n_a > 0 && <span style={{ color: "#5A7090", fontWeight: 700 }}>— {counts.n_a} N/A</span>}
          </div>
        </div>
        <div style={{ background: "rgba(255,255,255,0.06)", borderRadius: 100, height: 8, overflow: "hidden" }}>
          <div data-testid="readiness-bar" style={{ width: `${pct}%`, height: "100%", background: "linear-gradient(135deg, #00DC78 0%, #00FFFF 100%)", transition: "width 0.4s" }} />
        </div>
      </div>

      {grouped.map(({ tier, items: tierItems }) => (
        <div key={tier} style={{ marginBottom: "1.5rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.7rem" }}>
            <span style={{ fontSize: "1rem" }}>{TIER_META[tier].emoji}</span>
            <h3 style={{ color: TIER_META[tier].color, fontSize: "0.88rem", fontWeight: 800, margin: 0, letterSpacing: "0.04em", textTransform: "uppercase" }}>{TIER_META[tier].label}</h3>
          </div>
          <div style={{ display: "grid", gap: "0.65rem" }}>
            {tierItems.map(it => (
              <ChecklistItem key={it.item_id} item={it} onUpdate={updateItem} />
            ))}
          </div>
        </div>
      ))}
    </>
  );
};

const ChecklistItem = ({ item, onUpdate }) => {
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState(item.notes || "");
  const isDone = item.status === "done";
  const isNa = item.status === "n_a";

  const cycleStatus = () => {
    const next = item.status === "todo" ? "in_progress"
      : item.status === "in_progress" ? "done"
      : item.status === "done" ? "todo"
      : "todo";
    onUpdate(item.item_id, { status: next });
  };

  return (
    <div data-testid={`checklist-item-${item.item_id}`} style={{
      background: isDone ? "rgba(0,220,120,0.04)" : isNa ? "rgba(90,112,144,0.04)" : "rgba(255,255,255,0.025)",
      border: `1px solid ${isDone ? "rgba(0,220,120,0.20)" : "rgba(255,255,255,0.08)"}`,
      borderRadius: 8,
      padding: "0.85rem 1.05rem",
      opacity: isNa ? 0.55 : 1,
    }}>
      <div style={{ display: "flex", gap: "0.7rem", alignItems: "flex-start" }}>
        <button
          onClick={cycleStatus}
          data-testid={`status-toggle-${item.item_id}`}
          style={{
            background: "transparent",
            border: `2px solid ${STATUS_META[item.status].color}`,
            color: STATUS_META[item.status].color,
            width: 28, height: 28, minWidth: 28, borderRadius: 6,
            fontSize: "0.85rem", fontWeight: 800, cursor: "pointer", padding: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
          title={`${STATUS_META[item.status].label} — click to cycle`}
        >
          {item.status === "done" ? "✓" : item.status === "in_progress" ? "⚙" : item.status === "n_a" ? "—" : "○"}
        </button>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem", flexWrap: "wrap" }}>
            <h4 style={{ color: "#fff", fontSize: "0.92rem", fontWeight: 700, margin: 0, textDecoration: isDone ? "line-through" : "none" }}>{item.title}</h4>
            <div style={{ display: "flex", gap: "0.35rem", fontSize: "0.65rem" }}>
              <span style={{ background: "rgba(0,255,255,0.08)", color: "#5AC8D8", padding: "0.18rem 0.5rem", borderRadius: 4, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>{item.category}</span>
              <button onClick={() => setExpanded(!expanded)} style={{ background: "transparent", border: "none", color: "#7E92A8", fontSize: "0.7rem", cursor: "pointer", fontWeight: 700 }}>
                {expanded ? "▲ Less" : "▼ Why?"}
              </button>
            </div>
          </div>

          {expanded && (
            <div style={{ marginTop: "0.6rem", padding: "0.7rem 0.9rem", background: "rgba(0,0,0,0.18)", borderRadius: 6, border: "1px solid rgba(255,255,255,0.06)" }}>
              <div style={{ color: "#FFB347", fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "0.25rem" }}>Why it matters</div>
              <div style={{ color: "#ADBED1", fontSize: "0.82rem", lineHeight: 1.5, marginBottom: "0.7rem" }}>{item.why}</div>
              <div style={{ color: "#00DC78", fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "0.25rem" }}>Action</div>
              <div style={{ color: "#fff", fontSize: "0.82rem", lineHeight: 1.5, marginBottom: "0.7rem" }}>{item.action}</div>

              <label style={labelStyle}>Your notes</label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                onBlur={() => notes !== item.notes && onUpdate(item.item_id, { notes })}
                rows={2}
                data-testid={`notes-${item.item_id}`}
                placeholder="Phone numbers, broker names, links, dates…"
                style={{ ...cellInputStyle, fontFamily: "inherit", resize: "vertical" }}
              />
              <div style={{ display: "flex", gap: "0.4rem", marginTop: "0.55rem", flexWrap: "wrap" }}>
                <button onClick={() => onUpdate(item.item_id, { status: "n_a" })} data-testid={`mark-na-${item.item_id}`} style={{ background: "rgba(90,112,144,0.10)", border: "1px solid rgba(90,112,144,0.30)", color: "#5A7090", padding: "0.32rem 0.65rem", borderRadius: 5, fontSize: "0.7rem", fontWeight: 700, cursor: "pointer" }}>Not applicable</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Company Settings Section ───
const CompanySection = () => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/company-settings`, { credentials: "include" });
      if (r.ok) setS(await r.json());
    } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/company-settings`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(s),
      });
      if (r.ok) {
        setSavedAt(new Date().toLocaleTimeString());
        setTimeout(() => setSavedAt(null), 3500);
      }
    } catch {}
    setSaving(false);
  };

  if (!s) return <div style={{ color: "#7E92A8", textAlign: "center", padding: "2rem" }}>Loading…</div>;

  const Field = ({ k, label, placeholder = "", full = false, type = "text" }) => (
    <div style={{ gridColumn: full ? "1 / -1" : "auto" }}>
      <label style={labelStyle}>{label}</label>
      <input
        value={s[k] || ""}
        type={type}
        onChange={e => setS({ ...s, [k]: e.target.value })}
        placeholder={placeholder}
        data-testid={`company-${k}`}
        style={cellInputStyle}
      />
    </div>
  );

  return (
    <div style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", flexWrap: "wrap", gap: "0.5rem" }}>
        <h3 style={{ color: "#fff", fontSize: "1rem", fontWeight: 800, margin: 0 }}>Company Info — feeds every legal doc</h3>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          {savedAt && <span style={{ color: "#00DC78", fontSize: "0.74rem" }}>✓ Saved {savedAt}</span>}
          <button onClick={save} disabled={saving} data-testid="company-save" style={{ padding: "0.55rem 1.1rem", background: saving ? "rgba(255,255,255,0.08)" : "linear-gradient(135deg, #00DC78 0%, #00FFFF 100%)", border: "none", color: "#030B1A", borderRadius: 6, fontWeight: 800, fontSize: "0.82rem", cursor: saving ? "wait" : "pointer" }}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div style={{ color: "#FFB347", fontSize: "0.78rem", lineHeight: 1.5, marginBottom: "1rem", padding: "0.7rem 0.9rem", background: "rgba(255,179,71,0.06)", border: "1px solid rgba(255,179,71,0.25)", borderRadius: 6 }}>
        🔒 These fields auto-fill the W-9, Pre-Lien Notice, and Workers Comp documents. Update once — every PDF stays consistent.
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: "0.85rem" }}>
        <Field k="legal_name" label="Legal Entity Name" placeholder="Moovin Forward" />
        <Field k="dba_name" label="DBA / Assumed Name" placeholder="Moovin Forward" />
        <Field k="dba_county" label="DBA Filed In (County)" placeholder="Tarrant County" />
        <Field k="dba_filing_number" label="DBA Filing #" placeholder="A226004952" />
        <Field k="dba_filed_date" label="DBA Filed Date" placeholder="2026-04-08" />
        <Field k="individual_name" label="Owner / Managing Partner" placeholder="Shannon Antonette Ware" />
        <Field k="co_owner_name" label="Co-Owner / Co-Managing Partner" placeholder="Shaneka Latrice Paulette" />
        <Field k="phone" label="Phone" />
        <Field k="email" label="Email" />
        <Field k="address_line1" label="Street Address" full />
        <Field k="address_city_state_zip" label="City, State, ZIP" full />
        <Field k="state_of_formation" label="State of Formation" />
        <div>
          <label style={labelStyle}>LLC Filing Status</label>
          <select value={s.llc_status || "in_progress"} onChange={e => setS({ ...s, llc_status: e.target.value })} data-testid="company-llc_status" style={cellInputStyle}>
            <option value="in_progress">In Progress (operating as DBA)</option>
            <option value="filed">Filed (awaiting certificate)</option>
            <option value="active">Active</option>
          </select>
        </div>
        <Field k="llc_certificate_number" label="LLC Certificate #" placeholder="Provide on award" />
        <div>
          <label style={labelStyle}>EIN Status</label>
          <select value={s.ein_status || "pending"} onChange={e => setS({ ...s, ein_status: e.target.value })} data-testid="company-ein_status" style={cellInputStyle}>
            <option value="pending">Pending (submit on award)</option>
            <option value="active">Active</option>
          </select>
        </div>
        <Field k="ein" label="EIN (XX-XXXXXXX)" placeholder="Provide on award" />
        <Field k="tax_classification" label="Federal Tax Classification" full />
        <div>
          <label style={labelStyle}>GL Insurance Status</label>
          <select value={s.gl_status || "submit_on_award"} onChange={e => setS({ ...s, gl_status: e.target.value })} data-testid="company-gl_status" style={cellInputStyle}>
            <option value="submit_on_award">Submit COI on contract award</option>
            <option value="bound">Active / Bound</option>
            <option value="expired">Expired (renew required)</option>
          </select>
        </div>
        <Field k="gl_carrier" label="GL Insurance Carrier" placeholder="Provide on award" />
        <Field k="gl_policy_number" label="GL Policy Number" placeholder="Provide on award" />
        <Field k="gl_expiration" label="GL Expiration (YYYY-MM-DD)" placeholder="Provide on award" />
        <div>
          <label style={labelStyle}>Workers' Comp Status</label>
          <select value={s.wc_status} onChange={e => setS({ ...s, wc_status: e.target.value })} data-testid="company-wc_status" style={cellInputStyle}>
            <option value="not_decided">Not decided yet</option>
            <option value="non_subscriber">Non-Subscriber (use DWC-5 notice)</option>
            <option value="subscriber">Subscriber (carrying WC)</option>
          </select>
        </div>
      </div>
    </div>
  );
};

// ─── Vendor Tracker Section ───
const VendorsSection = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ firm_name: "", contact_name: "", contact_email: "", portal_url: "", status: "not_started", notes: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/vendor-registrations`, { credentials: "include" });
      if (r.ok) setVendors((await r.json()).vendors || []);
    } catch {}
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!draft.firm_name.trim()) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/vendor-registrations`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });
      if (r.ok) {
        setCreating(false);
        setDraft({ firm_name: "", contact_name: "", contact_email: "", portal_url: "", status: "not_started", notes: "" });
        load();
      }
    } catch {}
  };

  const updateStatus = async (vendor_id, status) => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/vendor-registrations/${vendor_id}`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      load();
    } catch {}
  };

  const remove = async (vendor_id) => {
    if (!confirm("Remove this vendor?")) return;
    try {
      await fetch(`${BACKEND_URL}/api/admin/vendor-registrations/${vendor_id}`, { method: "DELETE", credentials: "include" });
      load();
    } catch {}
  };

  const STATUS = {
    not_started: { label: "Not started", color: "#7E92A8" },
    applied: { label: "Applied", color: "#FFB347" },
    approved: { label: "Approved ✓", color: "#00DC78" },
    rejected: { label: "Rejected", color: "#FF6B6B" },
    needs_followup: { label: "Needs follow-up", color: "#FFB347" },
  };

  const SUGGESTED = ["Greystar", "Camden Property Trust", "Lincoln Property Company", "Mynd Property Management",
    "Invitation Homes", "AMH (American Homes 4 Rent)", "Northpoint Asset Management"];

  return (
    <>
      <div style={{ ...cardStyle, marginBottom: "1rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.7rem", flexWrap: "wrap", gap: "0.5rem" }}>
          <h3 style={{ color: "#fff", fontSize: "1rem", fontWeight: 800, margin: 0 }}>Vendor Pre-Registration Tracker</h3>
          <button onClick={() => setCreating(!creating)} data-testid="vendor-add-toggle" style={{ padding: "0.5rem 1rem", background: "linear-gradient(135deg, #00DC78 0%, #00FFFF 100%)", border: "none", color: "#030B1A", borderRadius: 6, fontWeight: 800, fontSize: "0.78rem", cursor: "pointer" }}>
            {creating ? "× Cancel" : "+ Add Firm"}
          </button>
        </div>
        <div style={{ color: "#7E92A8", fontSize: "0.78rem", lineHeight: 1.5 }}>
          Greystar/Camden/Lincoln require <strong style={{ color: "#FFB347" }}>30-60 day approvals</strong> before they'll send work. Start applications now so when your auto-pilot lands a meeting, you're already in their portal.
        </div>

        {creating && (
          <div style={{ marginTop: "0.85rem", padding: "0.85rem", background: "rgba(0,255,255,0.04)", borderRadius: 8, border: "1px solid rgba(0,255,255,0.15)" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "0.6rem", marginBottom: "0.6rem" }}>
              <input value={draft.firm_name} onChange={e => setDraft({ ...draft, firm_name: e.target.value })} placeholder="Firm name *" data-testid="vendor-firm-name" style={cellInputStyle} />
              <input value={draft.contact_name} onChange={e => setDraft({ ...draft, contact_name: e.target.value })} placeholder="Contact name" style={cellInputStyle} />
              <input value={draft.contact_email} onChange={e => setDraft({ ...draft, contact_email: e.target.value })} placeholder="Contact email" style={cellInputStyle} />
              <input value={draft.portal_url} onChange={e => setDraft({ ...draft, portal_url: e.target.value })} placeholder="Portal URL" style={cellInputStyle} />
            </div>
            <div style={{ marginBottom: "0.6rem", display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
              {SUGGESTED.map(n => (
                <button key={n} onClick={() => setDraft({ ...draft, firm_name: n })} style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.12)", color: "#5AC8D8", padding: "0.3rem 0.6rem", borderRadius: 4, fontSize: "0.7rem", fontWeight: 700, cursor: "pointer" }}>{n}</button>
              ))}
            </div>
            <button onClick={create} data-testid="vendor-create-btn" disabled={!draft.firm_name.trim()} style={{ padding: "0.5rem 1.1rem", background: !draft.firm_name.trim() ? "rgba(255,255,255,0.08)" : "linear-gradient(135deg, #00DC78 0%, #00FFFF 100%)", border: "none", color: "#030B1A", borderRadius: 6, fontWeight: 800, fontSize: "0.82rem", cursor: !draft.firm_name.trim() ? "not-allowed" : "pointer" }}>
              Add to Tracker
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div style={{ color: "#7E92A8", textAlign: "center", padding: "1.5rem" }}>Loading…</div>
      ) : vendors.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: "center", padding: "2rem" }}>
          <div style={{ color: "#fff", fontWeight: 700, marginBottom: "0.4rem" }}>No vendor registrations yet.</div>
          <div style={{ color: "#7E92A8", fontSize: "0.85rem" }}>Click <strong>+ Add Firm</strong> to start tracking your applications.</div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "0.85rem" }}>
          {vendors.map(v => (
            <div key={v.vendor_id} data-testid={`vendor-card-${v.vendor_id}`} style={cardStyle}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.5rem" }}>
                <h4 style={{ color: "#fff", fontSize: "0.95rem", fontWeight: 800, margin: 0 }}>{v.firm_name}</h4>
                <select value={v.status} onChange={e => updateStatus(v.vendor_id, e.target.value)} data-testid={`vendor-status-${v.vendor_id}`} style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${STATUS[v.status]?.color || "#7E92A8"}`, color: STATUS[v.status]?.color || "#7E92A8", padding: "0.25rem 0.4rem", borderRadius: 4, fontSize: "0.72rem", fontWeight: 700 }}>
                  {Object.entries(STATUS).map(([k, vv]) => <option key={k} value={k} style={{ background: "#030B1A", color: "#fff" }}>{vv.label}</option>)}
                </select>
              </div>
              {v.contact_name && <div style={{ color: "#ADBED1", fontSize: "0.78rem", marginBottom: "0.18rem" }}>👤 {v.contact_name}</div>}
              {v.contact_email && <div style={{ color: "#5AC8D8", fontSize: "0.76rem", marginBottom: "0.18rem" }}>{v.contact_email}</div>}
              {v.portal_url && <a href={v.portal_url} target="_blank" rel="noopener noreferrer" style={{ color: "#5AC8D8", fontSize: "0.74rem", textDecoration: "underline" }}>Portal ↗</a>}
              <div style={{ marginTop: "0.5rem", display: "flex", justifyContent: "flex-end" }}>
                <button onClick={() => remove(v.vendor_id)} data-testid={`vendor-delete-${v.vendor_id}`} style={{ background: "transparent", border: "1px solid rgba(255,107,107,0.20)", color: "#FF6B6B", padding: "0.25rem 0.55rem", borderRadius: 4, fontSize: "0.7rem", fontWeight: 700, cursor: "pointer" }}>🗑</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

// ─── Legal Docs Section ───
const DocsSection = () => {
  const [busy, setBusy] = useState("");
  const [showPreLien, setShowPreLien] = useState(false);
  const [pl, setPl] = useState({
    project_id: "",
    owner_name: "",
    owner_address: "",
    original_contractor_name: "N/A — direct contract with owner",
    original_contractor_address: "",
    property_description: "",
    work_description: "Interior unit stabilization including paint, flooring, fixtures, hardware, and turnover documentation services.",
    amount_unpaid: "TBD upon completion",
    first_labor_or_material_date: new Date().toISOString().slice(0, 10),
  });

  const downloadW9 = async () => {
    setBusy("w9");
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/legal-docs/w9`, { credentials: "include" });
      if (r.ok) {
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = "MF-W9-Packet.pdf";
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      }
    } catch {}
    setBusy("");
  };

  const downloadDWC5 = async () => {
    setBusy("dwc5");
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/legal-docs/dwc5-non-subscriber`, { credentials: "include" });
      if (r.ok) {
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = "MF-WC-NonSubscriber-Notice.pdf";
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      }
    } catch {}
    setBusy("");
  };

  const downloadPreLien = async () => {
    if (!pl.owner_name.trim() || !pl.owner_address.trim() || !pl.property_description.trim()) {
      alert("Owner name, owner address, and property description are required.");
      return;
    }
    setBusy("prelien");
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/legal-docs/pre-lien`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(pl),
      });
      if (r.ok) {
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = `MF-PreLien-${pl.project_id || "notice"}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      }
    } catch {}
    setBusy("");
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(310px, 1fr))", gap: "1rem" }}>
      <DocCard
        emoji="📄"
        title="W-9 Packet"
        desc="Send to any client requesting your tax info. Auto-fills from your Company Info."
        primaryLabel="Generate & Download"
        onPrimary={downloadW9}
        busy={busy === "w9"}
        testId="doc-w9"
        tier="Tier 2"
      />

      <DocCard
        emoji="🛡"
        title="WC Non-Subscriber Notice (DWC-5)"
        desc="REQUIRED if you don't carry workers' comp. Post on every jobsite, hand to every new hire."
        primaryLabel="Generate & Download"
        onPrimary={downloadDWC5}
        busy={busy === "dwc5"}
        testId="doc-dwc5"
        tier="Tier 1"
        critical
      />

      <DocCard
        emoji="⚖"
        title="Texas Pre-Lien Notice"
        desc="TX Property Code §53.056 — protects mechanic's lien rights. Send Day 1 of every commercial project."
        primaryLabel={showPreLien ? "Hide form" : "Open form"}
        onPrimary={() => setShowPreLien(!showPreLien)}
        testId="doc-prelien"
        tier="Tier 1"
        critical
      />

      {showPreLien && (
        <div style={{ ...cardStyle, gridColumn: "1 / -1" }}>
          <h4 style={{ color: "#fff", fontSize: "0.95rem", fontWeight: 800, marginTop: 0, marginBottom: "0.85rem" }}>Pre-Lien Notice — Fill once per project</h4>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: "0.85rem", marginBottom: "1rem" }}>
            {[
              ["project_id", "Project ID / Reference", "MF-2026-001"],
              ["owner_name", "Property Owner Name *", "Acme REIT LLC"],
              ["owner_address", "Owner Mailing Address *", "100 Main St, Dallas TX 75201"],
              ["original_contractor_name", "Original Contractor (or 'N/A — direct')", ""],
              ["original_contractor_address", "Original Contractor Address", ""],
              ["amount_unpaid", "Amount Unpaid (or 'TBD')", "TBD upon completion"],
              ["first_labor_or_material_date", "First Labor/Material Date", "YYYY-MM-DD"],
            ].map(([k, lbl, ph]) => (
              <div key={k}>
                <label style={labelStyle}>{lbl}</label>
                <input value={pl[k] || ""} onChange={e => setPl({ ...pl, [k]: e.target.value })} placeholder={ph} data-testid={`prelien-${k}`} style={cellInputStyle} />
              </div>
            ))}
          </div>
          <label style={labelStyle}>Property Description (street + legal if known) *</label>
          <textarea value={pl.property_description} onChange={e => setPl({ ...pl, property_description: e.target.value })} rows={2} data-testid="prelien-property" style={{ ...cellInputStyle, fontFamily: "inherit", marginBottom: "0.85rem" }} />

          <label style={labelStyle}>Work Description</label>
          <textarea value={pl.work_description} onChange={e => setPl({ ...pl, work_description: e.target.value })} rows={3} data-testid="prelien-work" style={{ ...cellInputStyle, fontFamily: "inherit", marginBottom: "0.85rem" }} />

          <button onClick={downloadPreLien} disabled={busy === "prelien"} data-testid="prelien-generate" style={{ padding: "0.6rem 1.2rem", background: busy === "prelien" ? "rgba(255,255,255,0.08)" : "linear-gradient(135deg, #00DC78 0%, #00FFFF 100%)", border: "none", color: "#030B1A", borderRadius: 6, fontWeight: 800, fontSize: "0.85rem", cursor: busy === "prelien" ? "wait" : "pointer" }}>
            {busy === "prelien" ? "Generating…" : "📄 Generate & Download"}
          </button>
          <div style={{ color: "#FFB347", fontSize: "0.74rem", marginTop: "0.6rem", lineHeight: 1.5 }}>
            ⚠ Send via U.S. Certified Mail, Return Receipt Requested, within 15 days of starting work on the project.
          </div>
        </div>
      )}
    </div>
  );
};

const DocCard = ({ emoji, title, desc, primaryLabel, onPrimary, busy = false, testId, tier, critical = false }) => (
  <div data-testid={testId} style={{
    ...cardStyle,
    borderColor: critical ? "rgba(255,107,107,0.30)" : "rgba(255,255,255,0.08)",
    background: critical ? "rgba(255,107,107,0.04)" : cardStyle.background,
  }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.4rem" }}>
      <div style={{ fontSize: "1.4rem" }}>{emoji}</div>
      <span style={{ background: critical ? "rgba(255,107,107,0.15)" : "rgba(0,255,255,0.08)", color: critical ? "#FF6B6B" : "#5AC8D8", fontSize: "0.62rem", fontWeight: 800, letterSpacing: "0.10em", textTransform: "uppercase", padding: "0.18rem 0.45rem", borderRadius: 4 }}>{tier}</span>
    </div>
    <h4 style={{ color: "#fff", fontSize: "1rem", fontWeight: 800, margin: "0 0 0.4rem" }}>{title}</h4>
    <p style={{ color: "#ADBED1", fontSize: "0.82rem", lineHeight: 1.5, margin: "0 0 0.85rem" }}>{desc}</p>
    <button onClick={onPrimary} disabled={busy} style={{ width: "100%", padding: "0.55rem 1rem", background: busy ? "rgba(255,255,255,0.08)" : "linear-gradient(135deg, #00DC78 0%, #00FFFF 100%)", border: "none", color: "#030B1A", borderRadius: 6, fontWeight: 800, fontSize: "0.82rem", cursor: busy ? "wait" : "pointer" }}>
      {busy ? "Generating…" : primaryLabel}
    </button>
  </div>
);

export default LaunchChecklistTab;
